import { useEffect, useMemo } from 'react';

import * as Sentry from '@sentry/browser';

import { PackageInclusionRecord, useGetPackageInclusionsByOffer } from './useGetPackageInclusionsByOffer';

export interface PackageInclusion {
  id: string;
  name: string;
  description: string;
  rrp: number | null;
  total_value: number | null;
  type: string;
  currencyisocode: string;
}

interface UseFetchPackageInclusionsParams {
  offerId: string;
  dealSalesforceId: string;
  currencyisocode: string;
  enabled?: boolean;
}

export function useFetchPackageInclusions({
  offerId,
  dealSalesforceId,
  currencyisocode,
  enabled = true,
}: UseFetchPackageInclusionsParams) {
  const packageInclusionsQuery = useGetPackageInclusionsByOffer({ offerId, enabled });

  useEffect(() => {
    if (!packageInclusionsQuery.error) {
      return;
    }

    Sentry.captureException(packageInclusionsQuery.error, {
      tags: {
        hook: 'useFetchPackageInclusions',
        offerId,
      },
    });
  }, [offerId, packageInclusionsQuery.error]);

  const inclusionsForDealMapped = useMemo(() => {
    const inclusionsForDeal = packageInclusionsQuery.data?.[dealSalesforceId] ?? [];

    return inclusionsForDeal.map((obj: PackageInclusionRecord): PackageInclusion => {
      const inclusion = obj.inclusion;
      return {
        id: inclusion.id,
        description: inclusion.description,
        rrp: inclusion.rrp,
        total_value: inclusion.total_value,
        type: inclusion.type,
        name: inclusion.name,
        currencyisocode,
      };
    });
  }, [currencyisocode, dealSalesforceId, packageInclusionsQuery.data]);

  return {
    ...packageInclusionsQuery,
    data: inclusionsForDealMapped,
  };
}
